import {
  Card,
  Collapse,
  Row,
  Col,
  Form,
  Input,
  Select,
  Cascader,
  InputNumber,
  Tooltip,
  Button,
  Modal,
  Table,
  Popconfirm,
  DatePicker,
  Upload, message,
} from 'antd';
import { any } from 'prop-types';
import { useEffect, useState } from 'react';
import { PlusOutlined, QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import FundingForm from './FundingForm';
import DictionaryMatching from '@/components/DictionaryMatching/DictionaryMatching';
import { uploadFileUrl } from '@/services/majorFinancing';
import { addDecimalNum } from '@/utils/utils';
import EquipmentExpandTable from './EquipmentExpandTable';

const { Panel } = Collapse;

const layoutRow = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};
const layoutRow1 = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};

function ProjectBasicInfo({
  formRef, area, dict, data,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [specialAgriculturalFoundsVos, setSpecialAgriculturalFoundsVos] = useState([]);
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    if (!isEmpty(data?.specialAgriculturalFoundsVos)) {
      setSpecialAgriculturalFoundsVos(data.specialAgriculturalFoundsVos);
    }
  }, [data]);

  const props = {
    name: 'file',
    accept: '.pdf',
    action: uploadFileUrl,
    headers: {
      authorization: 'authorization-text',
    },
    beforeUpload(file) {
      const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isLt2M = file.size / 1024 / 1024 < 200;

      if (!isPDF) {
        message.error('只能上传PDF文件！');
        return Upload.LIST_IGNORE;
      }
      if (!isLt2M) {
        message.error('请上传200M以下文件!');
        return Upload.LIST_IGNORE;
      }
      return isPDF && isLt2M;
    },
  };

  const getNewData = (newData) => {
    const totalAmount = addDecimalNum(newData, 'totalAmount'); // 总投资金额
    const provincialSubsidyFunds = addDecimalNum(newData, 'provincialSubsidyFunds'); // 省级专项资资金
    const cityFunding = addDecimalNum(newData, 'cityFunding');// 市县资金
    const selfFunding = addDecimalNum(newData, 'selfFunding'); // 企业自筹资金
    const otherFunding = addDecimalNum(newData, 'otherFunding'); // 其他资金
    setSpecialAgriculturalFoundsVos(newData);
    formRef.setFieldsValue({
      specialAgriculturalFoundsVos: newData,
      provincialSubsidyFunds,
      totalAmount,
      cityFunding,
      selfFunding,
      otherFunding,
    });
  };

  const onCancel = () => {
    setIsModalOpen(false);
  };
  const onOk = (row) => {
    const newData = [...specialAgriculturalFoundsVos];
    const index = newData.findIndex((item) => row.rowKey === item.rowKey);
    if (index !== -1) {
      const item = newData[index];
      newData.splice(index, 1, {
        ...item,
        ...row,
      });
    } else {
      newData.push({
        ...row,
        rowKey: newData.length + 1,
      });
    }
    setIsModalOpen(false);
    getNewData(newData);
  };

  const onOpen = (val) => {
    setIsModalOpen(true);
    setFormValues(val);
  };

  const onDel = (id) => {
    const temp = [...specialAgriculturalFoundsVos];
    const result = temp
      .filter((item) => item.rowKey !== id) // 过滤掉
      .map((item, index) => ({
        ...item,
        rowKey: index + 1, // 重新分配 rowKey
      }));
    getNewData(result);
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'rowKey',
      width: 60,
    },
    {
      title: '子项名称',
      dataIndex: 'projectName',
      render: (text) => <DictionaryMatching value={text} dictionary={dict?.specialProjectName} />,
    },
    {
      title: '建设内容',
      dataIndex: 'constructionContent',
    },
    {
      title: '投资总额(万元)',
      children: [
        {
          title: '合计',
          dataIndex: 'totalAmount',
          width: 120,
        },
        {
          title: '省级专项资金',
          dataIndex: 'provincialSubsidyFunds',
          width: 80,
        },
        {
          title: '市县资金',
          dataIndex: 'cityFunding',
          width: 80,
        },
        {
          title: '企业自筹资金',
          dataIndex: 'selfFunding',
          width: 80,
        },
        {
          title: '其他资金',
          dataIndex: 'otherFunding',
          width: 80,
        },
      ],
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 150,
      ellipsis: true,
    },
    {
      title: '操作',
      width: 100,
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => onOpen(record)} style={{ padding: 0 }}>编辑</Button>
          <Popconfirm title="确定删除当前选项吗?" okText="确定" cancelText="取消" onConfirm={() => onDel(record.rowKey)}>
            <Button type="link" style={{ padding: '0 0 0 8px', color: 'red' }}>删除</Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  /**
   * 生成图片列表
   */
  const generateFileInfo = (fileList) => fileList.map((file) => ({
    fileName: file.name || file.fileName,
    fileUrl: file?.response?.data || file.fileUrl || file.url,
  }));

  const onChange = ({ file, fileList }, type) => {
    formRef.setFieldsValue({
      [type]: generateFileInfo(fileList),
    });
    if (file.status === 'done') {
      message.success(`文件【${file.name}】上传成功`);
    } else if (file.status === 'error') {
      message.error('上传失败');
    }
  };

  function getValueFromEvent(e) {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  }

  return (
    <Card title="项目信息">
      <Collapse defaultActiveKey={['1', '2', '3', '4', '5', '6', '7', '8']}>
        <Panel key="1" header="项目概况">
          <Row>
            <Col span={12}>
              <Form.Item
                label="项目名称"
                name="projectName"
                rules={[
                  {
                    required: true,
                    message: '请输入项目名称',
                  },
                ]}
              >
                <Input placeholder="请输入项目名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="建设单位"
                name="projectUnit"
                rules={[
                  {
                    required: true,
                    message: '请输入建设单位',
                  },
                ]}
              >
                <Input placeholder="请输入建设单位" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="申报年度"
                name="declarationYear"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="建设性质"
                name="projectNature"
                rules={[
                  {
                    required: true,
                    message: '请输入建设性质',
                  },
                ]}
              >
                <Select
                  placeholder="请选择建设性质"
                  options={dict?.specialAgriculturalType}
                  fieldNames={{ label: 'name', value: 'val' }}
                  mode="multiple"
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={['ignore', 'projectRegion']}
                label="项目建设区域"
                rules={[
                  {
                    required: true,
                    message: '请选择项目建设区域',
                  },
                ]}
              >
                <Cascader
                  options={area}
                  placeholder="请选择项目建设区域"
                  fieldNames={{ label: 'name', value: 'id', children: 'children' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="建设期限"
                name="projectConstructionPeriodStart"
                rules={[
                  {
                    required: true,
                    message: '请选择建设期限',
                  },
                ]}
              >
                <DatePicker.RangePicker picker="month" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                label="项目建设地址"
                name="projectConstructionAddress"
                rules={[
                  {
                    required: true,
                    message: '请输入项目建设地址',
                  },
                ]}
              >
                <Input placeholder="请输入项目建设地址" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
        {/* <Panel key="2" header="项目背景与必要项"> */}
        {/*  <Row> */}
        {/*    <Col span={24}> */}
        {/*      <Form.Item */}
        {/*        {...layoutRow} */}
        {/*        label="行业背景" */}
        {/*        name="industryBackground" */}
        {/*        rules={[ */}
        {/*          { */}
        {/*            required: true, */}
        {/*            message: '请输入行业背景', */}
        {/*          }, */}
        {/*        ]} */}
        {/*      > */}
        {/*        <Input.TextArea maxLength={1000} showCount placeholder="请输入行业背景" /> */}
        {/*      </Form.Item> */}
        {/*    </Col> */}
        {/*    <Col span={24}> */}
        {/*      <Form.Item */}
        {/*        {...layoutRow} */}
        {/*        name="projectBackground" */}
        {/*        label="项目背景" */}
        {/*        rules={[ */}
        {/*          { */}
        {/*            required: true, */}
        {/*            message: '请输入项目背景', */}
        {/*          }, */}
        {/*        ]} */}
        {/*      > */}
        {/*        <Input.TextArea maxLength={1000} showCount placeholder="请输入项目背景" /> */}
        {/*      </Form.Item> */}
        {/*    </Col> */}
        {/*    <Col span={24}> */}
        {/*      <Form.Item */}
        {/*        {...layoutRow} */}
        {/*        name="projectEssential" */}
        {/*        label="项目必要项" */}
        {/*        rules={[ */}
        {/*          { */}
        {/*            required: true, */}
        {/*            message: '请输入项目必要项', */}
        {/*          }, */}
        {/*        ]} */}
        {/*      > */}
        {/*        <Input.TextArea maxLength={1000} showCount placeholder="请输入项目必要项" /> */}
        {/*      </Form.Item> */}
        {/*    </Col> */}
        {/*  </Row> */}
        {/* </Panel> */}
        <Panel key="3" header="项目建设方案">
          <Row>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                label="产品方案"
                name="projectScheme"
                rules={[
                  {
                    required: true,
                    message: '请输入产品方案',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入产品方案" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                name="technicalProposal"
                label="技术方案"
                rules={[
                  {
                    required: true,
                    message: '请输入技术方案',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入技术方案" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                name="constructionContent"
                label="建设内容"
                rules={[
                  {
                    required: true,
                    message: '请输入建设内容',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入建设内容" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
        <Panel key="4" header="投资估算与资金筹措">
          <Row>
            <Col span={12}>
              <Form.Item
                label={(
                  <span>
                      总投资金额
                        <Tooltip title="总投资金额 = 省级专项资金 + 市县资金 + 企业自筹资金 + 其他资金">
                          <QuestionCircleOutlined />
                        </Tooltip>
                  </span>
                )}
                name="totalAmount"
                rules={[
                  {
                    required: true,
                    message: '请输入总投资金额',
                  },
                ]}
              >
                <InputNumber addonAfter="万元" disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                required
                label="省级专项资金"
                name="provincialSubsidyFunds"
                rules={[
                  {
                    required: true,
                    message: '请输入省级专项资金',
                  },
                ]}
              >
                <InputNumber addonAfter="万元" disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="市县资金"
                name="cityFunding"
                rules={[
                  {
                    required: true,
                    message: '请输入市县资金',
                  },
                ]}
              >
                <InputNumber addonAfter="万元" disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="企业自筹资金"
                name="selfFunding"
                rules={[
                  {
                    required: true,
                    message: '请输入企业自筹资金',
                  },
                ]}
              >
                <InputNumber addonAfter="万元" style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="其他资金"
                name="otherFunding"
                rules={[
                  {
                    required: true,
                    message: '请输入其他资金',
                  },
                ]}
              >
                <InputNumber addonAfter="万元" style={{ width: '100%' }} disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                required
                label="项目建设资金使用方案表"
                name="specialAgriculturalFoundsVos"
                rules={[
                  {
                    required: true,
                    message: '请添加项目建设资金使用方案表',
                  },
                ]}
              >
                <Button type="primary" onClick={() => onOpen({})} icon={<PlusOutlined />}>
                  添加
                </Button>
                {
                  !isEmpty(specialAgriculturalFoundsVos)
                  && (
                    <Table
                      style={{ marginTop: 20 }}
                      rowKey="rowKey"
                      columns={columns}
                      dataSource={specialAgriculturalFoundsVos}
                      pagination={false}
                      size="small"
                      expandable={{
                        // eslint-disable-next-line react/no-unstable-nested-components
                        expandedRowRender: (record) => (
                          <EquipmentExpandTable data={record.specialAgriculturalFundsEquips || []} />
                        ),
                        rowExpandable: (record) => !isEmpty(record.specialAgriculturalFundsEquips),
                      }}
                    />
                  )
                }
              </Form.Item>
            </Col>
          </Row>
        </Panel>
        <Panel key="5" header="施工进度与组织管理">
          <Row>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                label="建设进度"
                name="constructionProgress"
                rules={[
                  {
                    required: true,
                    message: '请输入建设进度',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入建设进度" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                name="constructionOrganization"
                label="施工组织"
                rules={[
                  {
                    required: true,
                    message: '请输入施工组织',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入施工组织" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
        <Panel key="6" header="效益分析">
          <Row>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                label="经济效益分析"
                name="benefitAnalysis"
                rules={[
                  {
                    required: true,
                    message: '请输入经济效益分析',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入经济效益分析" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                name="socialAnalysis"
                label="社会效益分析"
                rules={[
                  {
                    required: true,
                    message: '请输入社会效益分析',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入社会效益分析" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
        <Panel key="7" header="风险评估与应对措施">
          <Row>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                label="市场风险"
                name="marketRisk"
                rules={[
                  {
                    required: true,
                    message: '请输入市场风险',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入市场风险" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                name="technicalRisk"
                label="技术风险"
                rules={[
                  {
                    required: true,
                    message: '请输入技术风险',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入技术风险" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                {...layoutRow}
                name="policyRisk"
                label="政策风险"
                rules={[
                  {
                    required: true,
                    message: '请输入政策风险',
                  },
                ]}
              >
                <Input.TextArea maxLength={1000} showCount placeholder="请输入政策风险" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
        <Panel key="8" header="项目申报书">
          <Row>
            <Col span={24}>
              <Form.Item
                style={{ display: 'none' }}
                name="projectApplication"
              />
              <Form.Item
                {...layoutRow1}
                label={(
                  <div style={{ lineHeight: '16px', textAlign: 'center' }}>
                    项目申报书
                    <br />
                    （仅支持PDF文件）
                  </div>
                )}
                name={['ignore', 'projectApplication']}
                valuePropName="fileList"
                getValueFromEvent={getValueFromEvent}
                rules={[
                  {
                    required: true,
                    message: '请上传项目申报书！',
                  },
                ]}
              >
                <Upload
                  {...props}
                  onChange={(e) => onChange(e, 'projectApplication')}
                >
                  <Button icon={<UploadOutlined />}>上传文件</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>
      <Modal
        title="申请省级资金添加"
        destroyOnClose
        footer={null}
        open={isModalOpen}
        width={1200}
        maskClosable={false}
        onCancel={onCancel}
      >
        <FundingForm onCancel={onCancel} onOk={onOk} data={formValues} dict={dict} />
      </Modal>
    </Card>
  );
}
ProjectBasicInfo.propTypes = {
  formRef: any,
  area: any,
  dict: any,
  data: any,
};
export default ProjectBasicInfo;
